import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { CreditLimitStatus, CreditsResponse } from '@shared/api';
import { getCredits } from '../api/client';
import { AppBrand } from '../components/AppBrand';
import { useShellState } from '../components/AppShell';
import { activeOrgName, displayNameFromUsername } from '../components/sidebar/AccountMenu';
import { describeAllowance, describeUsage } from '../lib/creditAdmin';
import { useAuth } from '../state/useAuth';

function Row({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="settings-row">
      <dt className="settings-row-label">{label}</dt>
      <dd className="settings-row-value">{children}</dd>
    </div>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children: ReactNode }): ReactElement {
  return (
    <section className="settings-section">
      <h2 className="credits-admin-subtitle">{title}</h2>
      {note ? <p className="credits-admin-muted">{note}</p> : null}
      <dl className="settings-list">{children}</dl>
    </section>
  );
}

const LIMIT_LABEL: Record<CreditLimitStatus['scope'], string> = {
  rep: 'Your limit',
  agency: 'Agency limit',
};

/** The rep's credits (docs/limits.md): one row per rule that applies to them. */
function CreditsRows({ credits, error }: { credits: CreditsResponse | null; error: string | null }): ReactElement {
  if (error) return <Row label="Credits">Could not load your credits.</Row>;
  if (!credits) return <Row label="Credits">Loading…</Row>;
  if (credits.unlimited) return <Row label="Credits">Unlimited. No limit applies to you.</Row>;
  return (
    <>
      <Row label="Proposals left">
        <span className="settings-credits-remaining" data-blocked={credits.blockedBy ? '' : undefined}>
          {credits.remaining ?? 0}
        </span>
        {credits.blockedMessage ? <p className="settings-credits-note">{credits.blockedMessage}</p> : null}
      </Row>
      {credits.limits.map((limit) => (
        <Row key={limit.ruleId} label={LIMIT_LABEL[limit.scope]}>
          {describeAllowance(limit)}
          <p className="settings-credits-note">{describeUsage(limit)}</p>
        </Row>
      ))}
    </>
  );
}

/**
 * Settings, opened from the sidebar's account menu: the rep's account, their
 * organization and their proposal credits on one page. Read-only: identity,
 * password and org membership are managed in VisitIQ; credit limits by the
 * credits admin (/admin/credits).
 */
export function SettingsView(): ReactElement {
  useShellState({});
  const auth = useAuth();
  const status = auth.status;
  const signedIn = Boolean(status?.signedIn && status.username);
  const [credits, setCredits] = useState<CreditsResponse | null>(null);
  const [creditsError, setCreditsError] = useState<string | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    getCredits()
      .then((next) => {
        if (live) setCredits(next);
      })
      .catch((cause: unknown) => {
        if (live) setCreditsError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      live = false;
    };
    // Credits are per active agency, so a switch in the account menu refetches.
  }, [signedIn, status?.activeOrganizationId]);

  if (!status || !signedIn || !status.username) {
    return (
      <main className="credits-admin settings">
        <h1 className="credits-admin-title">Settings</h1>
        <p className="credits-admin-muted">Sign in to see your settings.</p>
      </main>
    );
  }

  return (
    <main className="credits-admin settings">
      <h1 className="credits-admin-title">Settings</h1>

      <Section title="Account" note="Your name, email and password come from your VisitIQ login.">
        <Row label="Name">{displayNameFromUsername(status.username)}</Row>
        <Row label="Email">{status.username}</Row>
        <Row label="Password">Change it in the VisitIQ app.</Row>
      </Section>

      <Section title="Organization" note="Org membership is managed in VisitIQ. Ask an administrator to change it.">
        <Row label="Organization">{activeOrgName(status)}</Row>
        <Row label="Brand">
          <span className="settings-brand">
            <AppBrand branding={status.branding} />
          </span>
        </Row>
        <Row label="Rate card">{status.activeRateCard?.label ?? 'None'}</Row>
        {status.organizations.length > 1 ? (
          <Row label="Your organizations">
            <ul className="settings-orgs">
              {status.organizations.map((org) => (
                <li key={org.id}>
                  {org.name}
                  {org.id === status.activeOrganizationId ? <span className="settings-active"> · active</span> : null}
                </li>
              ))}
            </ul>
          </Row>
        ) : null}
      </Section>

      <Section title="Proposal credits" note="One credit is used the first time a proposal is built.">
        <CreditsRows credits={credits} error={creditsError} />
      </Section>

      <button type="button" className="settings-signout" disabled={auth.busy} onClick={() => void auth.signOut()}>
        Log out
      </button>
    </main>
  );
}
